import React, { useState, useMemo } from "react";
import {
  Box,
  Select,
  MenuItem,
  SelectChangeEvent,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { LineSeries } from "@nivo/line";
import { useTheme } from "@mui/material/styles";
import { area, curveMonotoneX } from "d3-shape";
import dayjs from "dayjs";
import { useCompanyPriceToFcfHistory } from "../hooks/useCompanyPriceToFcfHistory";
import { getChartColors } from "../constants/chartColors";
import { evenlySpacedTickValues } from "../utils/evenlySpacedTickValues";
import { useStockCurrency } from "../../../../contexts/StockCurrencyContext";
import { formatDisplayCurrencyValue, formatDisplayCurrencyValueCompact } from "../../../../utils/stockPriceFormatter";
import GenericFinancialLineChart from "./shared/GenericFinancialLineChart";
import ChartToggleButton from "./shared/ChartToggleButton";
import ChartTooltip from "./shared/ChartTooltip";
import { hapticSelection } from "../../../../lib/haptics";

const PFCF_SERIES_ID = "P/FCF";
const PRICE_SERIES_ID = "Price";

const YEAR_OPTIONS = [
  { label: "1 Year", value: 1 },
  { label: "2 Years", value: 2 },
  { label: "3 Years", value: 3 },
  { label: "5 Years", value: 5 },
  { label: "10 Years", value: 10 },
  { label: "15 Years", value: 15 },
  { label: "20 Years", value: 20 },
];

const DEFAULT_YEARS = 1;

/**
 * Linear scaling of a price value into the P/FCF y-domain so a single nivo
 * y-axis can render both lines while still preserving each series' shape.
 * The returned `scale` is also used by the right-axis layer to project price
 * tick values back to the chart's pixel y-coordinates.
 */
const buildPriceScale = (
  priceValues: number[],
  pfcfMin: number,
  pfcfMax: number,
): { priceMin: number; priceMax: number; scale: (price: number) => number } | null => {
  const finite = priceValues.filter((p) => Number.isFinite(p));
  if (finite.length === 0) return null;

  let priceMin = Math.min(...finite);
  let priceMax = Math.max(...finite);
  if (priceMin === priceMax) {
    // Add a tiny pad so the line lands in the middle of the chart instead of on an edge.
    priceMin -= 1;
    priceMax += 1;
  }

  const pfcfRange = pfcfMax - pfcfMin;
  const priceRange = priceMax - priceMin;

  return {
    priceMin,
    priceMax,
    scale: (price: number) => pfcfMin + ((price - priceMin) / priceRange) * pfcfRange,
  };
};

const createPfcfAreaLayer = (color: string) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const PfcfAreaLayer = ({ series, innerHeight, innerWidth }: any) => {
    const instanceId = React.useId().replace(/:/g, "");

    if (!series || series.length === 0) return null;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pfcfSerie = series.find((s: any) => s.id === PFCF_SERIES_ID);
    if (!pfcfSerie) return null;

    const points = pfcfSerie.data
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .filter((d: any) => d.position.x != null && d.position.y != null)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((d: any) => ({ x: d.position.x as number, y: d.position.y as number }));

    if (points.length < 2) return null;

    const clipId = `pfcf-area-clip-${instanceId}`;
    const gradientId = `pfcf-area-gradient-${instanceId}`;

    const areaGen = area<{ x: number; y: number }>()
      .x((d) => d.x)
      .y1((d) => d.y)
      .y0(innerHeight)
      .curve(curveMonotoneX);

    const path = areaGen(points);
    if (!path) return null;

    return (
      <g>
        <defs>
          <clipPath id={clipId}>
            <rect x={0} y={0} width={innerWidth} height={innerHeight} />
          </clipPath>
          <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.2} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <g clipPath={`url(#${clipId})`}>
          <path d={path} fill={`url(#${gradientId})`} />
        </g>
      </g>
    );
  };
  return PfcfAreaLayer;
};

interface ReferenceLineProps {
  value: number;
  color: string;
  dashed: boolean;
  /**
   * X coordinate where the dashed line should begin. Used to leave a gap
   * for the inline label at the left edge — the line picks up *after* the
   * label so the two never overlap.
   */
  startX: number;
}

/**
 * Renders the dashed reference line, starting from `startX` so it leaves
 * room for an inline label at the left edge. Lives in the `customLayers`
 * slot (below the data line) so the chart line still passes cleanly over
 * the reference at intersection points.
 */
const createReferenceLineLayer = ({ value, color, dashed, startX }: ReferenceLineProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ReferenceLineLayer = ({ yScale, innerWidth }: any) => {
    if (!Number.isFinite(value) || typeof yScale !== "function") return null;
    const y = yScale(value);
    if (!Number.isFinite(y)) return null;
    if (startX >= innerWidth) return null;

    return (
      <line
        x1={startX}
        x2={innerWidth}
        y1={y}
        y2={y}
        stroke={color}
        strokeWidth={1.2}
        strokeDasharray={dashed ? "4 4" : undefined}
        opacity={0.7}
      />
    );
  };
  return ReferenceLineLayer;
};

interface PriceAxisLayerProps {
  priceMin: number;
  priceMax: number;
  scale: (price: number) => number;
  textColor: string;
  formatPrice: (v: number) => string;
}

const createPriceAxisLayer = ({ priceMin, priceMax, scale, textColor, formatPrice }: PriceAxisLayerProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const PriceAxisLayer = ({ yScale, innerWidth }: any) => {
    if (typeof yScale !== "function") return null;

    const tickCount = 4;
    const ticks: number[] = [];
    for (let i = 0; i <= tickCount; i++) {
      ticks.push(priceMin + ((priceMax - priceMin) * i) / tickCount);
    }

    return (
      <g>
        {ticks.map((price, i) => {
          const y = yScale(scale(price));
          if (!Number.isFinite(y)) return null;
          return (
            <text
              key={i}
              x={innerWidth + 8}
              y={y + 3}
              textAnchor="start"
              fontSize={10}
              fontWeight={600}
              fill={textColor}
            >
              {formatPrice(price)}
            </text>
          );
        })}
      </g>
    );
  };
  return PriceAxisLayer;
};

interface HistoricPriceToFcfChartProps {
  ticker: string;
  height?: number;
  isModal?: boolean;
  onCloseModal?: () => void;
  renderCard?: boolean;
  showFullscreen?: boolean;
  noHover?: boolean;
}

const HistoricPriceToFcfChart: React.FC<HistoricPriceToFcfChartProps> = ({
  ticker,
  height = 320,
  isModal = false,
  onCloseModal,
  renderCard = true,
  showFullscreen = false,
  noHover = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const chartColors = getChartColors(isDark);
  // Align with the PE Ratio chart so both ratio charts share Insight's
  // signature green and read as a coherent pair on the Financials tab.
  const pfcfColor = chartColors.positive;
  const priceColor = chartColors.neutral;
  // Reference lines are colour-coded to read like a valuation traffic light:
  //   max    → red (expensive end of the band)
  //   median → amber (neutral midline)
  //   min    → green (cheap end of the band)
  // The chip dots in the legend below pick up the same colours, so the chart
  // itself no longer needs inline "Max/Median/Min" text labels — keeping the
  // plot area uncluttered.
  const maxColor = chartColors.negative;
  const medianColor = chartColors.warning;
  const minColor = chartColors.positive;

  const { currency } = useStockCurrency();

  const [years, setYears] = useState(DEFAULT_YEARS);
  const [showMedian, setShowMedian] = useState(true);
  const [showMin, setShowMin] = useState(true);
  const [showMax, setShowMax] = useState(true);
  const [showPrice, setShowPrice] = useState(false);

  const { data: response, isLoading, error } = useCompanyPriceToFcfHistory(ticker, years);

  const stats = useMemo(() => {
    if (!response?.data?.length) return null;
    const values = response.data
      .map((p) => p.priceToFcf)
      .filter((v) => Number.isFinite(v))
      .sort((a, b) => a - b);
    if (values.length === 0) return null;
    return {
      min: values[0],
      max: values[values.length - 1],
      median: values[Math.floor(values.length / 2)],
      pfcfMin: values[0],
      pfcfMax: values[values.length - 1],
    };
  }, [response]);

  const priceScale = useMemo(() => {
    if (!response?.data?.length || !stats) return null;
    return buildPriceScale(
      response.data.map((p) => p.price),
      stats.pfcfMin,
      stats.pfcfMax,
    );
  }, [response, stats]);

  const chartData = useMemo((): LineSeries[] => {
    if (!response?.data?.length) return [];

    const pfcfSeries = {
      id: PFCF_SERIES_ID,
      color: pfcfColor,
      data: response.data.map((point) => ({
        x: dayjs(point.date).format("DD MMM YYYY"),
        y: point.priceToFcf,
        price: point.price,
        ttmFcfPerShare: point.ttmFcfPerShare,
      })),
    } as LineSeries;

    const series: LineSeries[] = [pfcfSeries];

    if (showPrice && priceScale) {
      series.push({
        id: PRICE_SERIES_ID,
        color: priceColor,
        data: response.data.map((point) => ({
          x: dayjs(point.date).format("DD MMM YYYY"),
          y: priceScale.scale(point.price),
          actualPrice: point.price,
        })),
      } as LineSeries);
    }

    return series;
  }, [response, pfcfColor, priceColor, showPrice, priceScale]);

  const PfcfAreaLayer = useMemo(() => createPfcfAreaLayer(pfcfColor), [pfcfColor]);

  const formatPfcf = (v: number) => `${v.toFixed(1)}x`;

  const buildReferenceLine = ({
    show,
    value,
    color,
    dashed = true,
  }: {
    show: boolean;
    value: number | null | undefined;
    color: string;
    dashed?: boolean;
  }) => {
    if (!show || value == null || !Number.isFinite(value)) return null;
    return createReferenceLineLayer({ value, color, dashed, startX: 0 });
  };

  const medianLine = useMemo(
    () => buildReferenceLine({ show: showMedian, value: stats?.median, color: medianColor }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showMedian, stats, medianColor],
  );

  const minLine = useMemo(
    () => buildReferenceLine({ show: showMin, value: stats?.min, color: minColor }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showMin, stats, minColor],
  );

  const maxLine = useMemo(
    () => buildReferenceLine({ show: showMax, value: stats?.max, color: maxColor }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showMax, stats, maxColor],
  );

  const PriceAxisLayer = useMemo(() => {
    if (!showPrice || !priceScale) return null;
    return createPriceAxisLayer({
      priceMin: priceScale.priceMin,
      priceMax: priceScale.priceMax,
      scale: priceScale.scale,
      textColor: priceColor,
      // Backend pre-normalizes prices via computePriceToReportedCurrencyMultiplier
      // (e.g. GBp → GBP), so this axis receives values already in display currency.
      formatPrice: (v) => formatDisplayCurrencyValueCompact(v, currency),
    });
  }, [showPrice, priceScale, priceColor, currency]);

  const customLayers = useMemo(() => {
    // Layers that render BEHIND the data line: gradient fill, dashed
    // reference lines, and the right-side price axis labels. Reference
    // values are colour-coded — see the chip legend below the chart for
    // which line is which.
    return [
      PfcfAreaLayer,
      medianLine,
      minLine,
      maxLine,
      PriceAxisLayer,
    ].filter(Boolean);
  }, [PfcfAreaLayer, medianLine, minLine, maxLine, PriceAxisLayer]);

  const axisBottomTickValues = useMemo(() => {
    const xValues = chartData[0]?.data.map((d) => String(d.x)) ?? [];
    if (xValues.length === 0) return undefined;
    return evenlySpacedTickValues(xValues, isMobile);
  }, [chartData, isMobile]);

  const isEmpty = chartData.length === 0 || chartData[0].data.length === 0;

  const handleYearsChange = (event: SelectChangeEvent<number>) => {
    hapticSelection();
    setYears(event.target.value as number);
  };

  const hoverBg = isDark ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.03)";

  // Mirrors the "Show" label style on HistoricPERatioChart so the two charts
  // read as a coherent pair on the Financials tab.
  const groupLabelSx = {
    fontSize: "0.8125rem",
    fontWeight: 500,
    color: theme.palette.text.secondary,
  } as const;

  const referenceChip = (args: {
    label: string;
    color: string;
    active: boolean;
    onToggle: () => void;
  }) => {
    return (
      <ChartToggleButton
        label={args.label}
        selected={args.active}
        onToggle={args.onToggle}
        color={args.color}
      />
    );
  };

  const periodSelectorEl = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Typography sx={groupLabelSx}>Show</Typography>
      <Select
        value={years}
        onChange={handleYearsChange}
        size="small"
        inputProps={{ "aria-label": "Price to free cash flow history range" }}
        sx={{
          minWidth: 130,
          height: 34,
          "@media (pointer: coarse)": { height: 50 },
          backgroundColor: "background.surface",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          "& .MuiSelect-select": {
            fontSize: "0.8125rem",
            fontWeight: 500,
            color: theme.palette.text.primary,
            py: 0.5,
            px: 1.5,
          },
          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
          "&:hover": {
            backgroundColor: hoverBg,
            borderColor: isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.15)",
          },
          "&.Mui-focused": {
            borderColor: theme.palette.primary.main,
            backgroundColor: "background.surface",
          },
          "& .MuiSelect-icon": { color: theme.palette.text.secondary },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              backgroundColor: "background.paper",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              mt: 0.5,
              boxShadow: "none",
              "& .MuiMenuItem-root": {
                fontSize: "0.8125rem",
                fontWeight: 500,
                color: theme.palette.text.primary,
                py: 1,
                px: 1.5,
                minHeight: "auto",
                "&:hover": { backgroundColor: hoverBg },
                "&.Mui-selected": {
                  backgroundColor: hoverBg,
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                },
              },
            },
          },
        }}
      >
        {YEAR_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );

  const referenceChipsEl = (
    // Order: Min → Median → Max so the chips read left-to-right as
    // cheap → fair → expensive, mirroring the green→amber→red palette.
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      {referenceChip({
        label: "Min",
        color: minColor,
        active: showMin,
        onToggle: () => setShowMin((v) => !v),
      })}
      {referenceChip({
        label: "Median",
        color: medianColor,
        active: showMedian,
        onToggle: () => setShowMedian((v) => !v),
      })}
      {referenceChip({
        label: "Max",
        color: maxColor,
        active: showMax,
        onToggle: () => setShowMax((v) => !v),
      })}
    </Box>
  );

  const priceToggleEl = (
    <ChartToggleButton
      label="Show price"
      selected={showPrice}
      onToggle={() => setShowPrice((value) => !value)}
    />
  );

  const belowChartContent = (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: { xs: "center", md: "space-between" },
        rowGap: 1.25,
        columnGap: 2,
        mt: 2,
      }}
    >
      {periodSelectorEl}
      {referenceChipsEl}
      {priceToggleEl}
    </Box>
  );

  const tooltipRenderer = (point: {
    data: {
      xFormatted: string | number;
      y: number;
      price?: number;
      ttmFcfPerShare?: number;
    };
    serieId: string;
    serieColor: string;
  }) => {
    const data = point.data;
    const rows = [
      {
        label: "P/FCF",
        value: `${(data.y ?? 0).toFixed(1)}x`,
        color: pfcfColor,
      },
    ];

    if (typeof data.price === "number" && Number.isFinite(data.price)) {
      rows.push({
        label: "Price",
        value: formatDisplayCurrencyValue({ price: data.price, currency }),
        color: priceColor,
      });
    }

    if (typeof data.ttmFcfPerShare === "number" && Number.isFinite(data.ttmFcfPerShare)) {
      rows.push({
        label: "TTM FCF / share",
        value: formatDisplayCurrencyValue({ price: data.ttmFcfPerShare, currency }),
        color: chartColors.neutral,
      });
    }

    return <ChartTooltip header={String(data.xFormatted)} rows={rows} />;
  };

  const colorPicker = (serie: LineSeries) =>
    serie.id === PFCF_SERIES_ID ? pfcfColor : priceColor;

  return (
    <GenericFinancialLineChart
      title="Historic Price / FCF (TTM)"
      ticker={ticker}
      height={height}
      isModal={isModal}
      renderCard={renderCard}
      showFullscreen={showFullscreen}
      noHover={noHover}
      onCloseModal={onCloseModal}
      isLoading={isLoading}
      error={error ?? null}
      chartData={chartData}
      isEmpty={isEmpty}
      enableCrosshair={false}
      lineWidth={2.5}
      pointSize={0}
      useSlices={true}
      customLayers={customLayers}
      bottomMargin={isMobile ? 60 : 70}
      rightMargin={showPrice ? (isMobile ? 56 : 72) : 48}
      axisBottomTickPadding={isMobile ? 14 : 16}
      axisLeftTickPadding={isMobile ? 12 : 10}
      axisBottomTickValues={axisBottomTickValues}
      yAxisLegend="Price / FCF"
      yAxisFormat={(value) => `${Number(value).toFixed(1)}x`}
      colors={colorPicker}
      tooltip={tooltipRenderer}
      belowChartContent={belowChartContent}
      hideLogoInModal
      noDataTitle="No Price/FCF Data Available"
      noDataDescription="A daily Price/FCF ratio cannot be computed for this company. This can occur when price, cash flow, or income statement data is unavailable for the selected period."
    />
  );
};

export default HistoricPriceToFcfChart;
